/**
 * AI findings — vendor-agnostic model for results ingested from DICOM SR / SEG
 * or direct API pushes. The viewer shows them in a right-side panel, lets the
 * radiologist accept/reject each one, and eventually renders geometry overlays
 * onto the images.
 *
 * Backend reference:
 *   ai.findings.list  { studyId, status? } → [{ findingId, ... }]
 *   ai.findings.setStatus { findingId, status: 'accepted'|'rejected'|'pending' }
 */
import { apiCall } from '../api/client';

export type FindingStatus = 'pending' | 'accepted' | 'rejected';
export type Severity = 'critical' | 'urgent' | 'routine';

export interface AiFinding {
  findingId: number;
  seriesId?: number;
  imageIndex?: number;
  vendor: string;
  model: string;
  finding: string;
  /** SNOMED / RadLex code if the vendor emits one. */
  code: string;
  /** 0..1 model confidence. */
  confidence: number;
  severity: Severity;
  /** JSON blob: bounding box, polygon, or reference to a SEG/GSPS SOP. Vendor-specific. */
  geometry: string;
  status: FindingStatus;
  reviewedBy?: number;
  reviewedAt?: string;
  createdAt: string;
}

export async function listAiFindings(
  studyId: number,
  status?: FindingStatus
): Promise<AiFinding[]> {
  const res = await apiCall<AiFinding>('ai.findings.list', { studyId, status: status || '' });
  return res.s === 'ok' ? (res.data || []) : [];
}

export async function setFindingStatus(
  findingId: number,
  status: FindingStatus
): Promise<boolean> {
  const res = await apiCall('ai.findings.setStatus', { findingId, status });
  return res.s === 'ok';
}

/** Severity ordering for sorting: critical first. */
export function severityRank(s: Severity): number {
  return s === 'critical' ? 0 : s === 'urgent' ? 1 : 2;
}

/** Human-friendly label for a confidence score (0..1). */
export function confidencePct(c: number): string {
  if (c < 0 || c > 1) return '—';
  return `${Math.round(c * 100)}%`;
}
